import React from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";

const ORIGIN_STYLE = {
    custom: { bgcolor: "#f0f9ff", color: "#0369a1" },
    system: { bgcolor: "#FFF8E1", color: "#b45309" },
    external: { bgcolor: "#fdf4ff", color: "#7e22ce" },
};

const EventTypesTab = ({
    eventTypesApi,
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
}) => {
    if (eventTypesApi.error) return <ErrorState error={eventTypesApi.error} onRetry={() => eventTypesApi.refetch()} />;
    if (eventTypesApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    if (!Array.isArray(eventTypesApi.data) || eventTypesApi.data.length === 0)
        return <EmptyState message="Aucun type d'événement configuré" color={SECTION_COLORS.eventTypes.main} />;

    const page = pages[4];
    const paginated = eventTypesApi.data.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <Grid container spacing={2}>
                {paginated.map((et) => {
                    const originStyle = ORIGIN_STYLE[et.origin] || { bgcolor: "#f5f5f5", color: "#555" };
                    const hasSchema = et.payload_schema && Object.keys(et.payload_schema).length > 0;

                    return (
                        <Grid item xs={12} sm={6} md={4} key={et.id}>
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.eventTypes.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.eventTypes.main}06 0%, transparent 100%)`,
                                transition: "all 0.25s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: `0 8px 25px ${SECTION_COLORS.eventTypes.main}12`,
                                    borderColor: `${SECTION_COLORS.eventTypes.main}30`,
                                },
                            }}>
                                {/* Origin badge */}
                                <Tooltip title={`Origine : ${et.origin}`} placement="left">
                                    <Chip label={et.origin} size="small"
                                        sx={{
                                            position: "absolute", top: 10, right: 10,
                                            fontWeight: 700, fontSize: 10, ...originStyle,
                                        }} />
                                </Tooltip>

                                {/* Header */}
                                <Box display="flex" alignItems="center" gap={1} mb={0.5} mr={9}>
                                    <EventNoteIcon sx={{ color: SECTION_COLORS.eventTypes.main, fontSize: 20, flexShrink: 0 }} />
                                    <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                        {et.name}
                                    </Typography>
                                </Box>

                                {/* Key + brand */}
                                <Chip label={et.key} size="small"
                                    sx={{
                                        mb: 1, fontSize: 10, fontWeight: 600,
                                        bgcolor: SECTION_COLORS.eventTypes.light,
                                        color: SECTION_COLORS.eventTypes.main, maxWidth: "100%"
                                    }} />

                                {et.description && (
                                    <Typography fontSize={11} color="text.secondary" mb={1.5} noWrap sx={{ fontStyle: "italic" }}>
                                        {et.description}
                                    </Typography>
                                )}

                                {/* Meta */}
                                <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                    {et.brand && (
                                        <Chip label={et.brand} size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#f0fdf4", color: "#15803d" }} />
                                    )}
                                    {hasSchema && (
                                        <Chip label="Schéma" size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#f0f9ff", color: "#0369a1" }} />
                                    )}
                                </Box>

                                {/* Footer */}
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={et.active} />
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(et)}
                                                sx={{
                                                    color: SECTION_COLORS.eventTypes.main, bgcolor: `${SECTION_COLORS.eventTypes.main}10`,
                                                    "&:hover": { bgcolor: `${SECTION_COLORS.eventTypes.main}20` }, width: 30, height: 30
                                                }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(et)}
                                                sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(et)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {eventTypesApi.data.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={eventTypesApi.data.length} page={page}
                    onPageChange={handlePageChange(4)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default EventTypesTab;
